import HeaderComponent from "@/components/Header/Header";
import BackButton from "@/components/widgets/common/BackButton";
import { Spin, Tag } from "antd";
import { useParams } from "react-router-dom";
import React, { useState, useRef } from "react";
import CustomInputField from "@/components/widgets/Inputs/CustomInputField";
import { EyeOutlined, SearchOutlined } from "@ant-design/icons";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { Column } from "primereact/column";
import { tableBody, tableHeader } from "@/styles/TableStyles";
import { Button } from "antd";
import { BodyContainer, Container } from "@/styles/ContainerStyles";
import { PencilIcon, TrashIcon, Users } from "lucide-react";
import {
  useResponderAdmins,
  useDeactivateResponderAdmin,
} from "@/hooks/useAdmin";
import DeactivateResponderAdminModal from "@/components/Modals/DeactivateResponderAdminModal";
import ViewResponderAdminModal from "@/components/Modals/ViewResponderAdminModal";

type ParamsType = {
  companyId?: string;
};

const ManageResponderAdmins = () => {
  // @ts-ignore
  const dt = useRef<DataTable>(null);
  const { companyId } = useParams<ParamsType>();

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<any>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedAdminForView, setSelectedAdminForView] = useState<any>(null);

  // Fetch responder admins data
  const {
    data: responderAdminsResponse,
    isLoading,
    isError,
    refetch,
  } = useResponderAdmins(page, limit, companyId || "");

  // Deactivate responder admin mutation
  const deactivateResponderAdminMutation = useDeactivateResponderAdmin();

  const adminsData = responderAdminsResponse?.data || [];
  const totalCount = responderAdminsResponse?.totalCount || 0;
  const totalPages = responderAdminsResponse?.totalPages || 0;
  const currentPage = responderAdminsResponse?.pageNumber || 1;

  const handleView = (rowData: any) => {
    setSelectedAdminForView(rowData);
    setIsViewModalOpen(true);
  };

  const handleEdit = (rowData: any) => {
    console.log("Editing:", rowData);
    // Implement edit logic
  };

  const handleDeactivate = (rowData: any) => {
    setSelectedAdmin(rowData);
    setIsDeactivateModalOpen(true);
  };

  const handleDeactivateConfirm = async (reason: string) => {
    if (!selectedAdmin) {
      console.error("No admin selected for deactivation");
      return;
    }

    try {
      const payload: IDeactivateResponderAdmin = {
        userId: selectedAdmin.id,
        reason: reason,
      };

      await deactivateResponderAdminMutation.mutateAsync(payload);

      // Refetch the data to show updated status
      await refetch();

      // Close the modal
      setIsDeactivateModalOpen(false);
      setSelectedAdmin(null);
    } catch (error) {
      console.error("Error deactivating responder admin:", error);
      // Error handling is already done in the hook
    }
  };

  const handleDeactivateModalClose = () => {
    setIsDeactivateModalOpen(false);
    setSelectedAdmin(null);
  };

  const handleViewModalClose = () => {
    setIsViewModalOpen(false);
    setSelectedAdminForView(null);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  const renderStatus = (status: number) => {
    switch (status) {
      case 0:
        return (
          <div className="flex items-center justify-center">
            <Tag color="green">Active</Tag>
          </div>
        );
      case 1:
        return (
          <div className="flex items-center justify-center">
            <Tag color="orange">Inactive</Tag>
          </div>
        );
      case 2:
        return (
          <div className="flex items-center justify-center">
            <Tag color="red">Deactivated</Tag>
          </div>
        );
      default:
        return <Tag color="blue">Unknown</Tag>;
    }
  };

  const renderActions = (rowData: any) => {
    return (
      <div className="flex gap-2">
        <Button
          type="link"
          size="small"
          className="text-blue-600 hover:text-blue-800"
          onClick={() => handleView(rowData)}
        >
          <div className="flex flex-row items-center justify-center gap-1">
            <EyeOutlined className="h-4 w-4 text-saferCity-primary" />
            <span>View</span>
          </div>
        </Button>
        <Button
          type="link"
          size="small"
          className="text-red-600 hover:text-red-800"
          onClick={
            rowData.status === 2 ? undefined : () => handleDeactivate(rowData)
          }
          disabled={rowData.status === 2}
        >
          <div className="flex flex-row items-center justify-center gap-1">
            <TrashIcon className="h-4 w-4" />
            <span>{rowData.status === 2 ? "Deactivated" : "Deactivate"}</span>
          </div>
        </Button>
      </div>
    );
  };

  return (
    <div className="w-full h-full">
      <Container className="w-full h-full">
        <BodyContainer className="w-full h-full">
          <Spin
            tip=""
            spinning={isLoading}
            delay={500}
            className="w-full h-full"
          >
            <div className="w-full h-full max-h-screen overflow-y-auto disable-scrollbars">
              <div className="flex items-center justify-start w-full  mb-4">
                <BackButton />
                <HeaderComponent title="Manage Responder Admins" />
              </div>

              <div className="flex flex-col   w-80 bg-backgroundColor mb-4 shadow-lg ml-4 p-2 rounded-lg">
                <div className="flex flex-row items-center justify-between mb-0 w-full h-full">
                  <p className="text-lg mb-0">Responder Admins</p>
                  <div className="flex h-6 w-3 justify-end">
                    <Users className="h-6 w-3 text-saferCity-primary" />
                  </div>
                </div>
                <p className="text-2xl text-start font-bold mb-0 leading-tight">
                  {adminsData.length}
                </p>
                <p className="text-sm text-start text-subtleTextColor mb-0 leading-tight">
                  Number of users that have been added
                </p>
              </div>

              {/* Main content container - centered */}
              <div className="flex flex-col items-center justify-start w-full h-full px-4">
                <div className="flex flex-col items-start justify-start w-full">
                  <p className="text-2xl font-bold mb-0 leading-tight">
                    {adminsData.length > 0
                      ? adminsData[0]?.responderCompany || "Company Name"
                      : "Company Name"}
                  </p>
                  <p className="text-sm text-subtleTextColor mb-0 leading-tight">
                    Responder Company Admins
                  </p>
                </div>
                <div className="w-full">
                  <DataTable
                    value={Array.isArray(adminsData) ? adminsData : []}
                    ref={dt}
                    paginator={totalPages > 1}
                    lazy
                    rows={limit}
                    totalRecords={totalCount}
                    first={(currentPage - 1) * limit}
                    onPage={(e) => setPage(Math.floor(e.first / e.rows) + 1)}
                    dataKey="id"
                    filters={undefined}
                    globalFilterFields={["firstName", "lastName", "status"]}
                    emptyMessage="No Responder Admins found."
                    rowHover
                    scrollable
                    scrollHeight="40rem"
                    className="shadow-md rounded-lg"
                    size="large"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate={`Showing ${
                      (currentPage - 1) * limit + 1
                    } to ${Math.min(
                      currentPage * limit,
                      totalCount
                    )} of ${totalCount} Admins`}
                    stateStorage="session"
                    stateKey="dt-state-responder-admins-table"
                  >
                    <Column
                      field="createdAt"
                      header="Date Added"
                      headerStyle={tableHeader}
                      style={{ background: "#FBFBFB" }}
                      bodyStyle={tableBody}
                      body={(rowData) => formatDate(rowData.createdAt)}
                    />
                    <Column
                      field="firstName"
                      header="First Name"
                      headerStyle={tableHeader}
                      style={{ background: "#FBFBFB" }}
                      bodyStyle={tableBody}
                    />
                    <Column
                      field="lastName"
                      header="Last Name"
                      headerStyle={tableHeader}
                      style={{ background: "#FBFBFB" }}
                      bodyStyle={tableBody}
                    />
                    <Column
                      field="responderCompany"
                      header="Responder Company"
                      headerStyle={tableHeader}
                      style={{ background: "#FBFBFB" }}
                      bodyStyle={tableBody}
                    />
                    <Column
                      field="status"
                      header="Status"
                      headerStyle={tableHeader}
                      style={{ background: "#FBFBFB" }}
                      bodyStyle={tableBody}
                      body={(rowData) => renderStatus(rowData.status)}
                    />
                    <Column
                      header="CTA"
                      headerStyle={tableHeader}
                      style={{ background: "#FBFBFB" }}
                      bodyStyle={tableBody}
                      body={renderActions}
                    />
                  </DataTable>
                </div>
              </div>
            </div>
          </Spin>
        </BodyContainer>
      </Container>

      {/* Deactivate Modal */}
      <DeactivateResponderAdminModal
        isOpen={isDeactivateModalOpen}
        onClose={handleDeactivateModalClose}
        onDeactivate={handleDeactivateConfirm}
        adminName={
          selectedAdmin
            ? `${selectedAdmin.firstName} ${selectedAdmin.lastName}`
            : "Responder Admin"
        }
        isLoading={deactivateResponderAdminMutation.isPending}
      />

      {/* View Modal */}
      <ViewResponderAdminModal
        isOpen={isViewModalOpen}
        onClose={handleViewModalClose}
        adminData={selectedAdminForView}
      />
    </div>
  );
};

export default ManageResponderAdmins;
